import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comic = useMemo(() => comics.find(c => c.id === id), [id]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 奇妙漫画网`;
    } else {
      document.title = '漫画未找到 - 奇妙漫画网';
    }
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const relatedComics = comics
    .filter(c => c.id !== comic.id && c.categories.some(cat => comic.categories.includes(cat)))
    .slice(0, 4);

  const displayedChapters = showAllChapters
    ? comic.chapters
    : comic.chapters.slice(-20);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicSeries',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `https://www.qimiaomanhua.com/article/${comic.id}`,
    image: comic.coverImage,
    genre: comic.categories,
    inLanguage: 'zh-CN',
    datePublished: comic.chapters[0]?.updateDate || '2025-01-01',
    dateModified: comic.updateDate,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 5,
      ratingCount: comic.favorites,
    },
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/ViewAction',
      userInteractionCount: comic.views,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.qimiaomanhua.com/' },
      { '@type': 'ListItem', position: 2, name: '漫画详情', item: `https://www.qimiaomanhua.com/article/${comic.id}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: comic.categories[0] === 'korean' ? '韩漫' : comic.categories[0] === 'japanese' ? '日漫' : '分类浏览', path: `/category/${comic.categories[0]}` },
              { label: comic.title },
            ]} />
          </div>

          {/* Comic Detail Header */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="w-full aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">{comic.title}</h1>
                <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                    ★ {comic.rating} 分
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {formatViews(comic.views)} 阅读
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {formatViews(comic.favorites)} 收藏
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-background-300 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map(tag => (
                    <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-background-200 text-foreground-600 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>

                <div className="flex items-center gap-3">
                  <button className="px-8 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    开始阅读第一话
                  </button>
                  <button className="px-6 py-3 bg-background-200 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Chapters */}
          <section className="bg-background-100 py-10">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-heading font-bold text-foreground-900">章节列表（共 {comic.chapters.length} 话）</h2>
                <span className="text-xs text-foreground-500">更新于 {comic.updateDate}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {displayedChapters.map(ch => (
                  <button
                    key={ch.id}
                    className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all duration-200 text-left cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700 truncate">{ch.title}</span>
                    <span className="text-xs text-foreground-400 ml-2 flex-shrink-0">{ch.updateDate}</span>
                  </button>
                ))}
              </div>

              {comic.chapters.length > 20 && (
                <div className="text-center mt-5">
                  <button
                    onClick={() => setShowAllChapters(!showAllChapters)}
                    className="px-6 py-2 bg-background-200 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {showAllChapters ? '收起章节' : `展开全部 ${comic.chapters.length} 话`}
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {relatedComics.map(c => (
                  <ComicCard key={c.id} comic={c} size="medium" />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}